import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../../i18n'
import { userApi } from '../../api'
import type { UserRelation } from '../../types/api'
import { useTheme } from '../../hooks/useTheme'
import { useAuth } from '../../contexts/AuthContext'
import { IconButton, FilterButton, Button, Badge, ErrorMessage, Loading } from '../ui'
import { UserRelationCard } from './UserRelationCard'

type ContactType = 'ALL' | 'COACH' | 'CLIENT'

interface Contact extends UserRelation {
  contactType: 'COACH' | 'CLIENT'
}

export default function Contacts() {
  const navigate = useNavigate()
  const theme = useTheme()
  const { t } = useTranslation()
  const { user } = useAuth()
  
  const [coaches, setCoaches] = useState<UserRelation[]>([])
  const [trainees, setTrainees] = useState<UserRelation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState<ContactType>('ALL')

  const hasCoaches = coaches.length > 0
  const hasTrainees = trainees.length > 0
  const hasBoth = hasCoaches && hasTrainees

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    setLoading(true)
    setError(null)
    try {
      const [coachesList, traineesList] = await Promise.all([
        userApi.getCoaches().catch(() => []),
        userApi.getTrainees().catch(() => []),
      ])
      setCoaches(coachesList)
      setTrainees(traineesList)
    } catch (err) {
      console.error('Error loading contacts:', err)
      setError(err instanceof Error ? err.message : 'Failed to load contacts')
    } finally {
      setLoading(false)
    }
  }

  // Combine coaches and trainees with type labels
  const allContacts: Contact[] = useMemo(() => {
    const coachesWithType: Contact[] = coaches.map(c => ({ ...c, contactType: 'COACH' as const }))
    const traineesWithType: Contact[] = trainees.map(t => ({ ...t, contactType: 'CLIENT' as const }))
    return [...coachesWithType, ...traineesWithType]
  }, [coaches, trainees])

  const filteredContacts = allContacts.filter(contact => {
    const query = searchQuery.toLowerCase()
    const matchesSearch = (
      contact.username?.toLowerCase().includes(query) ||
      contact.firstName?.toLowerCase().includes(query) ||
      contact.lastName?.toLowerCase().includes(query)
    )
    const matchesType = typeFilter === 'ALL' || contact.contactType === typeFilter
    return matchesSearch && matchesType
  })

  const inviteLabel = (t.trainees as { inviteButton?: string }).inviteButton ?? t.dashboard.quickActions.inviteTrainee ?? t.invite.title

  if (loading) {
    return <Loading fullScreen />
  }

  if (error) {
    return (
      <ErrorMessage
        title="Error"
        message={error}
        icon="⚠️"
        onRetry={loadData}
        fullScreen
      />
    )
  }

  return (
    <div className="p-5">
      <header className="mb-6 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-3xl font-bold mb-2">👥 {t.contacts.title}</h1>
        </div>
        {user?.hasTrainees && (
          <IconButton
            variant="primary"
            size="md"
            onClick={() => navigate('/invite')}
            title={inviteLabel}
            aria-label={inviteLabel}
          >
            ＋
          </IconButton>
        )}
      </header>

      <div className="mb-4 relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg opacity-50">🔍</span>
        <input
          type="text"
          placeholder={t.contacts.searchPlaceholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full rounded-xl pl-12 pr-4 py-3 text-base outline-none focus:border-2 transition-all"
          style={{ 
            backgroundColor: theme.inputBg,
            color: theme.textColor,
            border: `1px solid ${theme.inputBorder}`,
          }}
        />
      </div>

      {/* Type Filter - Only show if user has both coaches and trainees */}
      {hasBoth && (
        <div className="mb-6 grid grid-cols-3 gap-2">
          <FilterButton
            variant="all"
            isActive={typeFilter === 'ALL'}
            onClick={() => setTypeFilter('ALL')}
          >
            {t.contacts.filters.all}
          </FilterButton>
          <FilterButton
            variant="brand"
            isActive={typeFilter === 'COACH'}
            onClick={() => setTypeFilter('COACH')}
          >
            {t.contacts.filters.coaches}
          </FilterButton>
          <FilterButton
            variant="brand"
            isActive={typeFilter === 'CLIENT'}
            onClick={() => setTypeFilter('CLIENT')}
          >
            {t.contacts.filters.clients}
          </FilterButton>
        </div>
      )}

      {filteredContacts.length > 0 ? (
        <div className="flex flex-col gap-3">
          {filteredContacts.map(contact => {
            const displayName = contact.firstName 
              ? `${contact.firstName} ${contact.lastName || ''}`.trim()
              : contact.username || 'Unknown'
            
            const isCoach = contact.contactType === 'COACH'
            const detailPath = isCoach ? `/coach/${contact.id}` : `/trainee/${contact.id}`
            
            return (
              <UserRelationCard
                key={`${contact.contactType}-${contact.id}`}
                displayName={displayName}
                username={contact.username}
                photoUrl={contact.photoUrl}
                fallbackBg={isCoach ? theme.brand.medium : theme.accent.light}
                onClick={() => navigate(detailPath, { state: isCoach ? { coach: contact } : undefined })}
                badges={
                  <>
                    <Badge
                      variant="brand"
                      size="sm"
                      className="rounded-md flex-shrink-0"
                    >
                      {isCoach ? t.contacts.label.coach : t.contacts.label.client}
                    </Badge>
                    {contact.isVirtual && (
                      <Badge variant="brand" size="sm" className="rounded-md flex-shrink-0">
                        👤 {t.trainees.virtualBadge}
                      </Badge>
                    )}
                  </>
                }
              />
            )
          })}
        </div>
      ) : (
        <div className="text-center py-10 px-5">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-xl font-semibold mb-2">{t.contacts.emptySearch.title}</h3>
          <p className="mb-6 opacity-70">
            {searchQuery 
              ? t.contacts.emptySearch.tryDifferent 
              : hasCoaches && !hasTrainees
              ? t.myCoaches.emptyState.description
              : hasTrainees && !hasCoaches
              ? t.trainees.emptyState.description
              : t.contacts.emptySearch.noContacts}
          </p>
          {!searchQuery && user?.hasTrainees && (
            <Button
              variant="primary"
              size="md"
              onClick={() => navigate('/invite')}
            >
              ➕ {t.dashboard.emptyState.action}
            </Button>
          )}
          {!searchQuery && !user?.hasTrainees && !user?.hasCoaches && (
            <Button
              variant="primary"
              size="md"
              onClick={() => navigate('/become-specialist')}
            >
              ➕ {t.traineeDashboard.becomeCoach.title}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
